// src/components/dashboard/ReviewQueueDrawer.jsx
//
// Detail panel for one queued draft: who it is with, and what the reviewer
// has said about it so far.
//
// The comment thread is the same one the collab panel shows
// (GET /api/books/<id>/comments?type=draft). A lead reviewer is not a
// collaborator on the draft, so that read only works because _can_read_item
// in backend/routes/comments.py was widened for lead reviewers on
// pending_review drafts.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Drawer, Box, Typography, Chip, IconButton, Divider, Avatar,
  CircularProgress, Button, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE } from '../../utils/api';

function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/);
  return parts.map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

export default function ReviewQueueDrawer({ draft, headers, onClose, onAssignClick }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const draftId = draft?._id || null;

  const fetchComments = useCallback(async () => {
    if (!draftId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/books/${draftId}/comments?type=draft`,
        { credentials: 'omit', headers },
      );
      if (res.ok) {
        setComments(await res.json());
      } else if (res.status === 403) {
        // Distinguished from a generic failure because it means the backend
        // widening is missing or the caller is not actually a lead reviewer --
        // a very different fix from "the server is down".
        setError('You do not have access to this draft\u2019s comments.');
      } else {
        setError('Could not load comments.');
      }
    } catch {
      setError('Could not load comments.');
    } finally {
      setLoading(false);
    }
  }, [draftId, headers]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  return (
    <Drawer anchor="right" open={!!draft} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 440 }, maxWidth: '100vw' }} role="region" aria-label="Draft detail">
        {draft && (
          <>
            <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 3, position: 'relative' }}>
              <IconButton
                onClick={onClose}
                aria-label="Close"
                sx={{ position: 'absolute', right: 8, top: 8, color: 'inherit' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" sx={{ pr: 4, fontWeight: 600 }}>
                {draft.title || 'Untitled'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
                {[draft.authorName, draft.genre, draft.volume].filter(Boolean).join(' · ')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label={draft.assignedTo ? 'Assigned' : 'Unassigned'}
                  color={draft.assignedTo ? 'success' : 'warning'}
                />
                {draft.assignedUsername && (
                  <Chip size="small" label={draft.assignedUsername} color="secondary" />
                )}
              </Stack>
            </Box>

            <Box sx={{ p: 3 }}>
              <Typography variant="overline" color="text.secondary">Reviewer comments</Typography>

              {loading && <CircularProgress size={20} sx={{ display: 'block', mt: 2 }} />}

              {!loading && error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
              )}

              {!loading && !error && comments.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {draft.assignedTo
                    ? 'No comments left yet.'
                    : 'No reviewer assigned yet, so there is nothing to show here.'}
                </Typography>
              )}

              {!loading && !error && comments.map((c) => {
                const who = c.user?.name || c.user?.username || 'Reviewer';
                return (
                  <Box key={c.id} sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.main' }}>
                      {initials(who)}
                    </Avatar>
                    <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 1, p: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{who}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDateTime(c.createdAt)}
                        </Typography>
                        {c.page != null && <Chip size="small" variant="outlined" label={`p. ${c.page}`} />}
                        {c.resolved && <Chip size="small" variant="outlined" color="success" label="Resolved" />}
                      </Box>
                      <Typography variant="body2">{c.text}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Divider />

            <Box sx={{ p: 3 }}>
              <Button fullWidth variant="contained" onClick={() => onAssignClick(draft)}>
                {draft.assignedTo ? 'Reassign reviewer' : 'Assign reviewer'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
